import { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

// SEO keywords for different languages
const SEO_KEYWORDS = {
  en: 'vegan recipes, plant-based food, vegan cooking, healthy vegan meals, delicious vegan, french vegan food, vegan desserts, vegan meals, vegetarian recipes, cruelty-free food',
  fi: 'vegaani reseptit, vegaaniruoka, vegaani ruoka, terveellinen vegaani, ranskalainen vegaani, vegaani jälkiruoka, kasvisruoka, vegaaniset reseptit, kasvispohjainen ruoka',
  fr: 'recettes véganes, cuisine végétalienne, recettes végétaliennes, cuisine végane française, plats végans, desserts végans, nourriture végétalienne, recettes sans viande'
};

// Update document head with SEO meta tags
export const updateMetaTags = ({ title, description, image, url, type = 'website', keywords = '' }) => {
  // Update title
  document.title = title;
  
  // Helper to update/create meta tag
  const setMeta = (name, content, property = false) => {
    const attr = property ? 'property' : 'name';
    let meta = document.querySelector(`meta[${attr}="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attr, name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };
  
  // Standard meta tags
  setMeta('description', description);
  if (keywords) setMeta('keywords', keywords);
  
  // Open Graph
  setMeta('og:title', title, true);
  setMeta('og:description', description, true);
  setMeta('og:type', type, true);
  if (image) setMeta('og:image', image, true);
  if (url) setMeta('og:url', url, true);
  
  // Twitter Card
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);
  if (image) setMeta('twitter:image', image);
  
  // Canonical URL
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  if (url) canonical.setAttribute('href', url);
};

// Generate Recipe structured data (JSON-LD)
export const generateRecipeSchema = (recipe, getLocalizedText, currentLang) => {
  if (!recipe) return null;
  
  const title = getLocalizedText(recipe.title);
  const description = getLocalizedText(recipe.description);
  const ingredients = getLocalizedText(recipe.ingredients)
    .split('\n')
    .map(i => i.replace(/^[-•*]\s*/, '').trim())
    .filter(i => i.length > 0);
  const instructions = getLocalizedText(recipe.instructions)
    .split('\n')
    .filter(i => i.trim().length > 0)
    .map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      text: step.replace(/^\d+\.\s*/, '').trim()
    }));
  
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Recipe',
    name: title,
    description: description,
    image: recipe.image || 'https://love4vegan.com/icons/icon-512x512.png',
    author: {
      '@type': 'Organization',
      name: 'love4vegan'
    },
    datePublished: recipe.created_at || new Date().toISOString(),
    prepTime: recipe.prep_time ? `PT${recipe.prep_time}M` : undefined,
    cookTime: recipe.cook_time ? `PT${recipe.cook_time}M` : undefined,
    totalTime: recipe.prep_time && recipe.cook_time 
      ? `PT${recipe.prep_time + recipe.cook_time}M` 
      : undefined,
    recipeYield: recipe.servings ? `${recipe.servings} servings` : undefined,
    recipeCategory: recipe.course_type || 'main',
    recipeCuisine: 'Vegan',
    keywords: `vegan, plant-based, ${title}, vegaani, végétalien`,
    recipeIngredient: ingredients,
    recipeInstructions: instructions,
    suitableForDiet: 'https://schema.org/VeganDiet'
  };
  
  // Add nutrition if available
  if (recipe.nutrition?.macros) {
    schema.nutrition = {
      '@type': 'NutritionInformation',
      calories: recipe.nutrition.macros.calories ? `${recipe.nutrition.macros.calories} calories` : undefined,
      proteinContent: recipe.nutrition.macros.protein ? `${recipe.nutrition.macros.protein}g` : undefined,
      carbohydrateContent: recipe.nutrition.macros.carbs ? `${recipe.nutrition.macros.carbs}g` : undefined,
      fatContent: recipe.nutrition.macros.fat ? `${recipe.nutrition.macros.fat}g` : undefined,
      fiberContent: recipe.nutrition.macros.fiber ? `${recipe.nutrition.macros.fiber}g` : undefined
    };
  }
  
  // Add aggregate rating if available
  if (recipe.average_rating && recipe.rating_count) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: recipe.average_rating,
      ratingCount: recipe.rating_count,
      bestRating: 5,
      worstRating: 1
    };
  }
  
  return schema;
};

// Generate Website structured data
export const generateWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'love4vegan',
  url: 'https://love4vegan.com',
  description: 'Delicious vegan recipes for everyone. Discover healthy plant-based meals and start your vegan journey today!',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://love4vegan.com/recipes?search={search_term_string}',
    'query-input': 'required name=search_term_string'
  }
});

// Generate Organization structured data
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'love4vegan',
  url: 'https://love4vegan.com',
  logo: 'https://love4vegan.com/icons/icon-512x512.png',
  sameAs: [
    'https://www.instagram.com/love4vegan'
  ]
});

// Generate BreadcrumbList structured data
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

// Inject JSON-LD script
export const injectJsonLd = (schema, id) => {
  // Remove existing script with same id
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};

// Remove JSON-LD script
export const removeJsonLd = (id) => {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
};

// SEO Hook for pages
export const useSEO = ({ title, description, image, url, type, keywords, jsonLd, jsonLdId }) => {
  const { language } = useLanguage();
  
  useEffect(() => {
    // Combine provided keywords with default language keywords
    const allKeywords = keywords 
      ? `${keywords}, ${SEO_KEYWORDS[language] || SEO_KEYWORDS.en}`
      : SEO_KEYWORDS[language] || SEO_KEYWORDS.en;
    
    updateMetaTags({
      title,
      description,
      image,
      url,
      type,
      keywords: allKeywords
    });
    
    if (jsonLd && jsonLdId) {
      injectJsonLd(jsonLd, jsonLdId);
    }
    
    return () => {
      if (jsonLdId) {
        removeJsonLd(jsonLdId);
      }
    };
  }, [title, description, image, url, type, keywords, jsonLd, jsonLdId, language]);
};

// Default export component for convenience
export const SEO = ({ title, description, image, url, type, keywords, jsonLd, jsonLdId }) => {
  useSEO({ title, description, image, url, type, keywords, jsonLd, jsonLdId });
  return null;
};
